import type { z } from 'zod';
import { schemas } from '@delaybuddy/contract';
import type { ExpiredLink } from './contract';
import {
  API_BASE,
  type DocumentInput,
  type IntakeApi,
  type SearchParams,
  type UploadOptions,
} from './client';
import { ApiError, NetworkError, isTurnstileError, type ApiErrorBody } from './errors';
import { turnstileBroker, type TurnstileSource } from '../turnstile';

/** The contract's `TURNSTILE_HEADER`, written out (as API_BASE: the bundle stays free of it). */
export const TURNSTILE_HEADER = 'X-Turnstile-Token';

type FetchLike = typeof fetch;

export interface HttpApiOptions {
  fetch?: FetchLike;
  /**
   * Prefix for API URLs. '' in the browser (same origin). Tests pass `location.origin`,
   * because Node's fetch needs absolute URLs.
   */
  origin?: string;
  /**
   * Parse every response with the contract's Zod schemas (strips unknown keys, throws on a
   * shape the contract doesn't allow). Default: on in dev and tests, off in production so an
   * API that is ahead of the funnel never breaks a visitor's session.
   */
  validate?: boolean;
  /** XMLHttpRequest constructor for uploads (progress events need XHR). */
  xhr?: typeof XMLHttpRequest;
  /** A call's budget before it counts as a network failure (`NetworkError.timedOut`). */
  timeoutMs?: number;
  /** The same for an upload (a 10 MB photo on a slow connection). */
  uploadTimeoutMs?: number;
  /** Wait before the one retry of an idempotent write (tests: 0). */
  retryDelayMs?: number;
  /** Where Turnstile tokens for the guarded operations come from (default: the page's broker). */
  turnstile?: TurnstileSource;
}

/** 20 s: the UI budget w1-07 set for a call; the document check has its own polling. */
export const TIMEOUT_MS = 20_000;
export const UPLOAD_TIMEOUT_MS = 120_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT';
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  headers?: Record<string, string>;
}

/** The caller's signal plus the call's own budget: `timedOut()` tells the two apart. */
function withTimeout(signal: AbortSignal | undefined, ms: number) {
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = ms > 0 ? setTimeout(() => ((timedOut = true), ctrl.abort()), ms) : null;
  const onAbort = () => ctrl.abort();
  if (signal?.aborted) ctrl.abort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  return {
    signal: ctrl.signal,
    timedOut: () => timedOut,
    done() {
      if (timer) clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    },
  };
}

/** `Retry-After` in seconds (a number or an HTTP date), or null. */
function retryAfterOf(v: string | null): number | null {
  if (!v) return null;
  const n = Number(v);
  if (Number.isFinite(n)) return Math.max(0, n);
  const at = Date.parse(v);
  return Number.isNaN(at) ? null : Math.max(0, Math.round((at - Date.now()) / 1000));
}

/** Dev and test builds carry the contract's schemas; a production build doesn't (below). */
const SCHEMAS_BUILD = import.meta.env.DEV || import.meta.env.MODE === 'test';
const VALIDATE_DEFAULT = SCHEMAS_BUILD;

type Schemas = typeof schemas;
/**
 * The schemas, or null in a production build. Call sites name a schema with a selector
 * (`(s) => s.Enquiry`) instead of reading `schemas.Enquiry`, so nothing references them in
 * production: zod and every schema drop out of the bundle (≈ 45 kB gzip; vite.config.ts marks
 * the contract's modules side-effect free). Production never validates anyway (`validate`).
 */
const SCHEMAS: Schemas | null = SCHEMAS_BUILD ? schemas : null;
export type SchemaPick<S extends z.ZodType> = (s: Schemas) => S;

const enc = encodeURIComponent;

function url(origin: string, path: string, query?: RequestOptions['query']) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined) qs.set(k, String(v));
  return `${origin}${API_BASE}${path}${qs.size ? `?${qs}` : ''}`;
}

function parseJson(text: string): unknown {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const strOrNull = (v: unknown) => v === null || typeof v === 'string';

/** `error.fields`: field → messages. Anything else is ignored. */
function readFields(v: unknown): Record<string, string[]> | undefined {
  if (!isObject(v)) return undefined;
  const ok = Object.values(v).every((m) => Array.isArray(m) && m.every((x) => typeof x === 'string'));
  return ok ? (v as Record<string, string[]>) : undefined;
}

const LINK_REASONS: readonly unknown[] = ['expired', 'replaced', 'revoked'] satisfies ExpiredLink['reason'][];

/**
 * The 410's `error.details` (the contract's ExpiredLink), checked by hand: no zod in production.
 * A `reason` this build doesn't know (an API ahead of the funnel) reads as `revoked`, the
 * wording that claims nothing, rather than losing the details (and "Email me a new link").
 */
function readExpiredLink(v: unknown): ExpiredLink | undefined {
  if (!isObject(v)) return undefined;
  const ok =
    typeof v.reason === 'string' &&
    (v.audience === 'claimant' || v.audience === 'passenger') &&
    typeof v.enquiryId === 'string' &&
    strOrNull(v.claimRef) &&
    strOrNull(v.maskedEmail) &&
    strOrNull(v.claimantFirstName) &&
    typeof v.canRenew === 'boolean';
  if (!ok) return undefined;
  const link = v as unknown as ExpiredLink;
  return LINK_REASONS.includes(link.reason) ? link : { ...link, reason: 'revoked' };
}

/**
 * The error envelope → ApiError. Read loosely: a code the contract doesn't list yet (an API
 * ahead of the funnel) keeps its code and message. A body that isn't the envelope becomes
 * `http_<status>`.
 */
function toApiError(
  status: number,
  statusText: string,
  json: unknown,
  retryAfter: string | null = null,
): ApiError {
  const env = (json as { error?: Partial<ApiErrorBody> } | null)?.error;
  const after = status === 429 ? retryAfterOf(retryAfter) : null;
  if (!env || typeof env.code !== 'string' || typeof env.message !== 'string')
    return new ApiError(status, {
      code: `http_${status}`,
      message: statusText || 'Request failed',
      retryAfter: after,
    });
  return new ApiError(status, {
    code: env.code,
    message: env.message,
    fields: readFields(env.fields),
    details: status === 410 ? readExpiredLink(env.details) : undefined,
    retryAfter: after,
  });
}

/** The JSON as the schema reads it (when validating), else as it came. */
function parse<S extends z.ZodType>(pick: SchemaPick<S>, json: unknown, validate: boolean): z.output<S> {
  return validate && SCHEMAS ? pick(SCHEMAS).parse(json) : (json as z.output<S>);
}

/**
 * One JSON call against the Intake API. Same origin, so the session cookie rides along
 * (`credentials: 'same-origin'`). Non-2xx → ApiError; fetch failure → NetworkError.
 */
export async function request<S extends z.ZodType>(
  fetchImpl: FetchLike,
  origin: string,
  path: string,
  schema: SchemaPick<S> | null,
  opts: RequestOptions = {},
  validate = VALIDATE_DEFAULT,
): Promise<z.output<S>> {
  let res: Response;
  let text: string;
  const t = withTimeout(opts.signal, opts.timeoutMs ?? TIMEOUT_MS);
  try {
    res = await fetchImpl(url(origin, path, opts.query), {
      method: opts.method ?? 'GET',
      headers: {
        Accept: 'application/json',
        ...(opts.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...opts.headers,
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
      signal: t.signal,
    });
    text = await res.text();
  } catch (e) {
    if (t.timedOut()) throw new NetworkError(e, { timedOut: true });
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new NetworkError(e);
  } finally {
    t.done();
  }
  const json = parseJson(text);
  if (!res.ok) throw toApiError(res.status, res.statusText, json, res.headers.get('Retry-After'));
  if (!schema) return undefined as z.output<S>;
  return parse(schema, json, validate);
}

async function requestBlob(fetchImpl: FetchLike, origin: string, path: string, opts: RequestOptions) {
  let res: Response;
  const t = withTimeout(opts.signal, opts.timeoutMs ?? TIMEOUT_MS);
  try {
    res = await fetchImpl(url(origin, path, opts.query), {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
      credentials: 'same-origin',
      signal: t.signal,
    });
    if (!res.ok)
      throw toApiError(
        res.status,
        res.statusText,
        parseJson(await res.text()),
        res.headers.get('Retry-After'),
      );
    return await res.blob();
  } catch (e) {
    if (e instanceof ApiError) throw e;
    if (t.timedOut()) throw new NetworkError(e, { timedOut: true });
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new NetworkError(e);
  } finally {
    t.done();
  }
}

/** Multipart upload with progress (fetch has no upload progress, XHR does). */
function upload<S extends z.ZodType>(
  XHR: typeof XMLHttpRequest,
  origin: string,
  path: string,
  form: FormData,
  schema: SchemaPick<S>,
  opts: UploadOptions,
  validate: boolean,
  timeoutMs: number,
  headers: Record<string, string> = {},
): Promise<z.output<S>> {
  return new Promise((resolve, reject) => {
    const xhr = new XHR();
    xhr.open('POST', url(origin, path));
    xhr.timeout = timeoutMs;
    xhr.setRequestHeader('Accept', 'application/json');
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      const json = parseJson(xhr.responseText);
      if (xhr.status < 200 || xhr.status >= 300)
        return reject(toApiError(xhr.status, xhr.statusText, json, xhr.getResponseHeader('Retry-After')));
      opts.onProgress?.(1);
      try {
        resolve(parse(schema, json, validate));
      } catch (e) {
        reject(e);
      }
    };
    xhr.onerror = () => reject(new NetworkError(new Error('Upload failed')));
    xhr.ontimeout = () => reject(new NetworkError(new Error('Upload timed out'), { timedOut: true }));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    opts.signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(form);
  });
}

export function createHttpApi(options: HttpApiOptions | FetchLike = {}): IntakeApi {
  const o: HttpApiOptions = typeof options === 'function' ? { fetch: options } : options;
  const fetchImpl: FetchLike = o.fetch ?? ((...a) => fetch(...a));
  const origin = o.origin ?? '';
  const validate = o.validate ?? VALIDATE_DEFAULT;
  const XHR = o.xhr ?? globalThis.XMLHttpRequest;

  const timeoutMs = o.timeoutMs ?? TIMEOUT_MS;
  const uploadTimeoutMs = o.uploadTimeoutMs ?? UPLOAD_TIMEOUT_MS;
  const retryDelayMs = o.retryDelayMs ?? 800;
  const turnstile = o.turnstile ?? turnstileBroker;

  type Headers = Record<string, string>;
  const get = <S extends z.ZodType>(
    path: string,
    schema: SchemaPick<S>,
    query?: RequestOptions['query'],
    signal?: AbortSignal,
    headers?: Headers,
  ) => request(fetchImpl, origin, path, schema, { query, signal, timeoutMs, headers }, validate);
  /**
   * A write. A PUT is idempotent in this contract (it sets a state), so a PUT that never got an
   * answer (the network, a timeout, a gateway 502/503/504) is sent once more; a POST never is
   * (it may have been done: a second signature, a second email).
   */
  const send = async <S extends z.ZodType>(
    method: 'POST' | 'PUT',
    path: string,
    schema: SchemaPick<S> | null,
    body: unknown,
    headers?: Headers,
  ): Promise<z.output<S>> => {
    const once = () =>
      request(fetchImpl, origin, path, schema, { method, body, timeoutMs, headers }, validate);
    try {
      return await once();
    } catch (e) {
      const unanswered =
        e instanceof NetworkError || (e instanceof ApiError && [502, 503, 504].includes(e.status));
      if (method !== 'PUT' || !unanswered) throw e;
      await new Promise((r) => setTimeout(r, retryDelayMs));
      return once();
    }
  };
  const multipart = <S extends z.ZodType>(
    path: string,
    form: FormData,
    schema: SchemaPick<S>,
    opts: UploadOptions = {},
    headers?: Headers,
  ) => upload(XHR, origin, path, form, schema, opts, validate, uploadTimeoutMs, headers);

  /**
   * Turnstile on the operations the contract marks `x-turnstile` (w5-11, decision 94). A token
   * rides in X-Turnstile-Token: on every call (`every`, and `email` when an email goes), or
   * until this page has seen the enquiry's session pass (`once`: flight search, a typed flight,
   * uploads; also after S7 and the scan). Refused (`turnstile_required` / `turnstile_failed`:
   * the API did nothing): once more with a fresh token, then the error goes to the screen.
   */
  const trusted = new Set<string>();
  const trust = (enquiryId: string) => trusted.add(enquiryId);
  async function guarded<T>(enquiryId: string | null, call: (headers: Headers) => Promise<T>): Promise<T> {
    const withToken = async () => {
      const token = await turnstile.token();
      return call(token ? { [TURNSTILE_HEADER]: token } : {});
    };
    let result: T;
    try {
      result = await (enquiryId && trusted.has(enquiryId) ? call({}) : withToken());
    } catch (e) {
      if (!isTurnstileError(e)) throw e;
      if (enquiryId) trusted.delete(enquiryId);
      result = await withToken();
    }
    if (enquiryId) trust(enquiryId);
    return result;
  }
  /** `every` and `email`: a fresh token on each call. */
  const everyCall = <T>(call: (headers: Headers) => Promise<T>) => guarded(null, call);

  const E = (id: string) => `/enquiries/${enc(id)}`;
  const P = (id: string, n: number) => `${E(id)}/passengers/${n}`;
  const search = ({ q, limit }: SearchParams) => ({ q, limit });

  const documentForm = (input: DocumentInput) => {
    const form = new FormData();
    form.set('file', input.file);
    form.set('purpose', input.purpose);
    if (input.itemKey) form.set('itemKey', input.itemKey);
    return form;
  };

  return {
    async searchAirports(p) {
      return (await get('/airports', (s) => s.AirportList, search(p), p.signal)).items;
    },
    async searchAirlines(p) {
      return (await get('/airlines', (s) => s.AirlineList, search(p), p.signal)).items;
    },

    createEnquiry: (body) => send('POST', '/enquiries', (s) => s.Enquiry, body),
    createEnquiryFromScan({ file, prefillContactFrom, attribution }, opts) {
      const form = new FormData();
      form.set('file', file);
      if (prefillContactFrom) form.set('prefillContactFrom', prefillContactFrom);
      for (const [k, v] of Object.entries(attribution ?? {})) if (v) form.set(k, v);
      return everyCall((h) => multipart('/enquiries/scan', form, (s) => s.ScanStarted, opts, h)).then((r) => {
        trust(r.enquiry.id); // the API trusts this session for the new check
        return r;
      });
    },
    getEnquiry: (id, opts) => get(E(id), (s) => s.Enquiry, undefined, opts?.signal),
    getNextStep: (id, query, opts) =>
      get(`${E(id)}/next-step`, (s) => s.NextStep, { ...query }, opts?.signal),
    getResume: (id, opts) => get(`${E(id)}/resume`, (s) => s.Resume, undefined, opts?.signal),

    updateTrip: (id, body) => send('PUT', `${E(id)}/trip`, (s) => s.Enquiry, body),
    searchLegFlights: (id, leg, query, opts) =>
      guarded(id, (h) =>
        get(`${E(id)}/legs/${leg}/flights`, (s) => s.FlightSearchResult, { ...query }, opts?.signal, h),
      ),
    lookupLegFlight: (id, leg, query, opts) =>
      guarded(id, (h) =>
        get(
          `${E(id)}/legs/${leg}/flights/lookup`,
          (s) => s.FlightLookupResult,
          { ...query },
          opts?.signal,
          h,
        ),
      ),
    chooseLeg: (id, leg, body) =>
      body.choice === 'manual'
        ? guarded(id, (h) => send('PUT', `${E(id)}/legs/${leg}`, (s) => s.Enquiry, body, h))
        : send('PUT', `${E(id)}/legs/${leg}`, (s) => s.Enquiry, body),
    setResultResponse: (id, body) => send('PUT', `${E(id)}/result-response`, (s) => s.Enquiry, body),
    setSelfReport: (id, body) => send('PUT', `${E(id)}/self-report`, (s) => s.Enquiry, body),
    setLegalAnswer: (id, body) => send('PUT', `${E(id)}/legal-answer`, (s) => s.Enquiry, body),
    setContact: (id, body) =>
      send('PUT', `${E(id)}/contact`, (s) => s.ContactResult, body).then((r) => {
        trust(id); // S7's own token passed: the API trusts this session for the check
        return r;
      }),
    setEligibilityBasis: (id, body) => send('PUT', `${E(id)}/eligibility-basis`, (s) => s.Enquiry, body),
    submitDataDispute: (id, body) => send('POST', `${E(id)}/data-dispute`, (s) => s.Enquiry, body),

    setPassengers: (id, body) => send('PUT', `${E(id)}/passengers`, (s) => s.Enquiry, body),
    addPassenger: (id, body) => send('POST', `${E(id)}/passengers`, (s) => s.Enquiry, body),
    setBookingReference: (id, body) => send('PUT', `${E(id)}/booking-reference`, (s) => s.Enquiry, body),

    getAssignmentPreview: (id, query, opts) =>
      get(`${E(id)}/assignment`, (s) => s.AssignmentPreview, { ...query }, opts?.signal),
    getAssignmentPdf: (id, query, opts) =>
      requestBlob(fetchImpl, origin, `${E(id)}/assignment/pdf`, {
        query: { ...query },
        signal: opts?.signal,
      }),
    signClaim: (id, body) => send('POST', `${E(id)}/signature`, (s) => s.Enquiry, body),
    signForMinor: (id, n, body) => send('POST', `${P(id, n)}/signature-for-minor`, (s) => s.Enquiry, body),
    setHandoff: (id, n, body) => send('PUT', `${P(id, n)}/handoff`, (s) => s.Enquiry, body),
    sendPassengerLink: (id, n, body) =>
      body.via === 'email'
        ? everyCall((h) => send('POST', `${P(id, n)}/link`, (s) => s.PassengerLinkResult, body, h))
        : send('POST', `${P(id, n)}/link`, (s) => s.PassengerLinkResult, body),

    decideItem: (id, key, body) => send('PUT', `${E(id)}/items/${enc(key)}/decision`, (s) => s.Enquiry, body),
    setExtra: (id, body) => send('PUT', `${E(id)}/extra`, (s) => s.Enquiry, body),
    uploadDocument: (id, input, opts) =>
      guarded(id, (h) => multipart(`${E(id)}/documents`, documentForm(input), (s) => s.Document, opts, h)),
    getDocument: (id, docId, opts) =>
      get(`${E(id)}/documents/${enc(docId)}`, (s) => s.Document, undefined, opts?.signal),

    getClaim: (id, query, opts) => get(`${E(id)}/claim`, (s) => s.ClaimView, { ...query }, opts?.signal),
    setWhatsapp: (id, body) => send('PUT', `${E(id)}/whatsapp`, (s) => s.Enquiry, body),
    async postEvents(id, body) {
      await send('POST', `${E(id)}/events`, null, body);
    },

    getPassengerPart: (id, n, opts) =>
      get(`${P(id, n)}/part`, (s) => s.PassengerPart, undefined, opts?.signal),
    getPartAssignmentPreview: (id, n, opts, query) =>
      get(
        `${P(id, n)}/part/assignment`,
        (s) => s.AssignmentPreview,
        query ? { ...query } : undefined,
        opts?.signal,
      ),
    getPartAssignmentPdf: (id, n, opts) =>
      requestBlob(fetchImpl, origin, `${P(id, n)}/part/assignment/pdf`, { signal: opts?.signal }),
    signPart: (id, n, body) => send('POST', `${P(id, n)}/part/signature`, (s) => s.PassengerPart, body),
    uploadPartDocument(id, n, { file }, opts) {
      const form = new FormData();
      form.set('file', file);
      return guarded(id, (h) => multipart(`${P(id, n)}/part/documents`, form, (s) => s.Document, opts, h));
    },
    getPartDocument: (id, n, docId, opts) =>
      get(`${P(id, n)}/part/documents/${enc(docId)}`, (s) => s.Document, undefined, opts?.signal),
    deferPartId: (id, n, body) =>
      body.via === 'email'
        ? everyCall((h) => send('POST', `${P(id, n)}/part/id-later`, (s) => s.PartLaterResult, body, h))
        : send('POST', `${P(id, n)}/part/id-later`, (s) => s.PartLaterResult, body),

    exchangeLink: (token) => send('POST', '/auth/exchange', (s) => s.ExchangeResponse, { token }),
    renewLink: (token) => everyCall((h) => send('POST', '/auth/renew', (s) => s.RenewResponse, { token }, h)),

    resolveReferral: (body) => send('POST', '/referrals', (s) => s.Referral, body),
  };
}
